import { flagEmoji } from "@shared/flag";
import { courseDateAt } from "@shared/course";
import {
  apiHallOfFame,
  apiLeaderboard,
  apiStreaks,
  type HofEntry,
  type LeaderboardEntry,
  type StreakEntry,
} from "../api";

type Tab = "today" | "streaks" | "hof";

export interface LeaderboardPageOptions {
  myHandle: string | null;
  /** Initial courseDate for the Daily tab; extracted from the URL in
   *  `/leaderboard/:date` form, else defaults to today. */
  initialDate: string | null;
}

export function mountLeaderboardPage(opts: LeaderboardPageOptions): void {
  const root = document.getElementById("lb-page");
  if (!root) return;
  root.hidden = false;
  document.body.classList.add("lb-page-open");

  const dateInput = document.getElementById("lb-date-input") as HTMLInputElement;
  const tabs = Array.from(
    root.querySelectorAll<HTMLButtonElement>(".lb-page-tab"),
  );
  const listEl = document.getElementById("lb-page-list")!;
  const contextEl = document.getElementById("lb-page-context")!;

  const todayDate = courseDateAt(Date.now());
  let date = opts.initialDate ?? todayDate;
  let tab: Tab = "today";
  dateInput.value = date;
  dateInput.max = todayDate;

  // Keep URL in sync so the browser back button works + direct
  // sharing of `/leaderboard/YYYY-MM-DD` deep links the user there.
  const syncUrl = () => {
    const want =
      tab === "today"
        ? date === todayDate
          ? "/leaderboard"
          : `/leaderboard/${date}`
        : `/leaderboard/${tab}`;
    if (window.location.pathname !== want) {
      window.history.replaceState({}, "", want);
    }
  };

  const render = async (): Promise<void> => {
    contextEl.textContent = "";
    listEl.innerHTML = `<div class="lb-page-empty">Loading…</div>`;
    syncUrl();

    try {
      if (tab === "today") {
        contextEl.innerHTML = `Daily course <strong>${escapeHtml(date)}</strong>${
          date === todayDate ? " · live" : " · archive"
        }`;
        const res = await apiLeaderboard(date, 100);
        renderToday(listEl, res.entries, opts.myHandle);
      } else if (tab === "hof") {
        contextEl.innerHTML = `<strong>All-time best round.</strong> Lowest total strokes across every daily course played. Ties broken by rounds played.`;
        const res = await apiHallOfFame(100);
        renderHof(listEl, res.entries, opts.myHandle);
      } else {
        contextEl.innerHTML = `<strong>Consecutive daily courses played.</strong> Current streak counts back from today; longest is the all-time record.`;
        const res = await apiStreaks(100);
        renderStreaks(listEl, res.entries, opts.myHandle);
      }
    } catch {
      listEl.innerHTML = `<div class="lb-page-empty">Couldn't load leaderboard.</div>`;
    }
  };

  for (const btn of tabs) {
    btn.addEventListener("click", () => {
      const next = (btn.dataset.tab ?? "today") as Tab;
      if (next === tab) return;
      tab = next;
      for (const b of tabs) b.classList.toggle("active", b === btn);
      void render();
    });
  }

  dateInput.addEventListener("change", () => {
    const v = dateInput.value;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    date = v;
    tab = "today";
    for (const b of tabs) b.classList.toggle("active", b.dataset.tab === "today");
    void render();
  });

  // If the permalink already names streaks / hof, honor it.
  const initialTab = detectTabFromPath();
  if (initialTab && initialTab !== "today") {
    tab = initialTab;
    for (const b of tabs) b.classList.toggle("active", b.dataset.tab === initialTab);
  }

  void render();
}

function detectTabFromPath(): Tab | null {
  const path = window.location.pathname;
  if (/\/leaderboard\/streaks\/?$/.test(path)) return "streaks";
  if (/\/leaderboard\/hof\/?$/.test(path)) return "hof";
  return null;
}

function renderToday(
  listEl: HTMLElement,
  entries: LeaderboardEntry[],
  myHandle: string | null,
): void {
  if (!entries.length) {
    listEl.innerHTML = `<div class="lb-page-empty">No scores yet for this day.</div>`;
    return;
  }
  listEl.innerHTML = entries
    .map((e, i) => {
      const rank = i + 1;
      const rankGlyph =
        rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;
      const rankClass = rank <= 3 ? `rank r${rank}` : "rank";
      return rowHtml({
        you: !!myHandle && e.handle === myHandle,
        rank: rankGlyph,
        rankClass,
        handle: e.handle,
        countryCode: e.countryCode,
        metric: String(e.totalStrokes),
        sub: "strokes",
      });
    })
    .join("");
}

function renderHof(
  listEl: HTMLElement,
  entries: HofEntry[],
  myHandle: string | null,
): void {
  if (!entries.length) {
    listEl.innerHTML = `<div class="lb-page-empty">No rounds completed yet.</div>`;
    return;
  }
  listEl.innerHTML = entries
    .map((e, i) => {
      const rank = i + 1;
      const rankGlyph =
        rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;
      const rankClass = rank <= 3 ? `rank r${rank}` : "rank";
      return rowHtml({
        you: !!myHandle && e.handle === myHandle,
        rank: rankGlyph,
        rankClass,
        handle: e.handle,
        countryCode: e.countryCode,
        metric: String(e.bestStrokes),
        sub: `${escapeHtml(e.bestDate)} · ${e.rounds} ${e.rounds === 1 ? "round" : "rounds"}`,
      });
    })
    .join("");
}

function renderStreaks(
  listEl: HTMLElement,
  entries: StreakEntry[],
  myHandle: string | null,
): void {
  const live = entries.filter((e) => e.current > 0 || e.longest > 1);
  if (!live.length) {
    listEl.innerHTML = `<div class="lb-page-empty">Nobody's on a streak yet. Play two days in a row to start one.</div>`;
    return;
  }
  listEl.innerHTML = live
    .map((e, i) => {
      const rank = i + 1;
      const rankGlyph =
        rank === 1 ? "🔥" : rank === 2 ? "🔥" : rank === 3 ? "🔥" : `#${rank}`;
      const rankClass = rank <= 3 ? `rank r${rank}` : "rank";
      return rowHtml({
        you: !!myHandle && e.handle === myHandle,
        rank: rankGlyph,
        rankClass,
        handle: e.handle,
        countryCode: e.countryCode,
        metric: String(e.current),
        sub: `longest ${e.longest}`,
      });
    })
    .join("");
}

function rowHtml(args: {
  you: boolean;
  rank: string;
  rankClass: string;
  handle: string;
  countryCode: string;
  metric: string;
  sub: string;
}): string {
  const initial = (args.handle[0] ?? "?").toUpperCase();
  const hash =
    (args.handle.charCodeAt(0) ?? 0) + (args.handle.charCodeAt(1) ?? 0);
  const hue = (hash % 4) + 1;
  return `
    <div class="lb-page-row${args.you ? " you" : ""}">
      <div class="${args.rankClass}">${args.rank}</div>
      <div class="avatar avatar-h${hue}">
        ${escapeHtml(initial)}
        <span class="flag">${escapeHtml(flagEmoji(args.countryCode))}</span>
      </div>
      <div class="handle">${escapeHtml(args.handle)}</div>
      <div class="metric">${escapeHtml(args.metric)}</div>
      <div class="sub">${args.sub}</div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
